import React, { useState } from "react";
import { Box, Typography } from "@material-ui/core";
import { Form, Formik } from "formik";
import clsx from "clsx";
import {
  GetAllBranches,
  GetAllLMSCourses,
  GetTeam,
} from "../../../../Services";
import {
  ReskinningRadioGroup,
  CustomCard,
  CustomAsyncAutocomplete,
  ReskinningInputs,
  CustomSwitch,
} from "../../../../Components";
import { useTranslate } from "../../../../Hooks";

import useStyles from "./styles";

function SchemaSettingsForm({ formikRef, generateStepperActions, formValues }) {
  const { translate } = useTranslate("NewAgentsRotationCriteria");
  const styles = useStyles();
  const [restrictBranches, setRestrictBranches] = useState(true);

  return (
    <Box marginTop="32px">
      <Box className={styles.stepperActionContainer}>
        <div>
          <Typography
            className={styles.secondaryTitle}
            variant="h6"
            component="h2"
          >
            {translate("schema-settings-form-title")}
          </Typography>
          <p className={styles.subText}>
            {translate("schema-settings-form-subtitle")}
          </p>
        </div>
        <Box
          display="flex"
          flexDirection="row-reverse"
          style={{ gap: "12px", maxHeight: "40px" }}
        >
          {generateStepperActions()}
        </Box>
      </Box>
      <Formik
        initialValues={{
          isOverrideLeadCapacitySettingEnabled: "true",
          rotationSchemeLeadDistributionType: "4",
          sla: null,
          rotationSchemeBranches: [],
          rotationSchemeTeams: [],
          rotationSchemeCourses: [],
          ...formValues,
        }}
        innerRef={formikRef}
      >
        {({ values, handleChange, setFieldValue }) => (
          <Form>
            <Box className={styles.contentWrapper}>
              <CustomCard
                boxShadow="xs"
                borderRadius="xl"
                borderColor="secondary"
                classes={styles.settingsForm}
              >
                <Box
                  display="flex"
                  alignItems={"center"}
                  className={styles.fieldWrapper}
                >
                  <span className={styles.radioGroupLabel}>
                    {translate("lead-distribution-strategy")}
                  </span>
                  <ReskinningRadioGroup
                    name="isOverrideLeadCapacitySettingEnabled"
                    rootCustomClasses={styles.radioRootWrapper}
                    data={[
                      {
                        value: "3",
                        label: translate("dist-strategy-all-system"),
                      },
                      {
                        value: "1",
                        label: translate(
                          "dist-strategy-current-schema-assigned"
                        ),
                      },
                      {
                        value: "2",
                        label: translate("dist-strategy-current-schema-active"),
                      },
                      {
                        value: "4",
                        label: translate("dist-strategy-one-by-one"),
                      },
                    ]}
                    idRef="Actions"
                    onSelectedRadioChanged={(e) => {
                      setFieldValue(
                        "rotationSchemeLeadDistributionType",
                        e.target.value
                      );
                    }}
                    value={values.rotationSchemeLeadDistributionType}
                    labelInput="label"
                    valueInput="value"
                  />
                </Box>
                <Box
                  display="flex"
                  alignItems={"center"}
                  className={styles.fieldWrapper}
                >
                  <span className={styles.radioGroupLabel}>
                    {translate("agent-load-limit-rule")}
                  </span>
                  <ReskinningRadioGroup
                    rootCustomClasses={styles.radioRootWrapper}
                    data={[
                      {
                        value: "false",
                        label: translate("agent-load-all-system"),
                      },
                      {
                        value: "true",
                        label: translate("agent-load-current-schema"),
                      },
                    ]}
                    idRef="Actions"
                    onSelectedRadioChanged={(e) => {
                      setFieldValue(
                        "isOverrideLeadCapacitySettingEnabled",
                        e.target.value
                      );
                    }}
                    value={values.isOverrideLeadCapacitySettingEnabled}
                    name="Active"
                    labelInput="label"
                    valueInput="value"
                    themeClass="theme-line"
                  />
                </Box>
                <Box
                  display="flex"
                  alignItems={"center"}
                  className={styles.fieldWrapper}
                >
                  <span
                    className={clsx(
                      styles.radioGroupLabel,
                      styles.alignSelfStart
                    )}
                  >
                    {translate("restrict-to-branches")}
                  </span>
                  <Box className={styles.fieldContainer}>
                    <CustomSwitch
                      checked={restrictBranches}
                      onChange={() => {
                        setFieldValue("rotationSchemeTeams", []);
                        setFieldValue("rotationSchemeCourses", []);
                        setFieldValue("rotationSchemeBranches", []);

                        setRestrictBranches(!restrictBranches);
                      }}
                    />
                    <CustomAsyncAutocomplete
                      rootCustomClasses={styles.inputwrapper}
                      autocompleteClasses={clsx({
                        [styles.disabled]: !restrictBranches,
                      })}
                      placeholder={translate("branch-input-placeholder")}
                      onFetchData={GetAllBranches}
                      chipsLabel={(option) => option.branchName || ""}
                      displayLabel={(option) => option.branchName || ""}
                      getOptionSelected={(option, value) =>
                        option?.branchId === value?.branchId
                      }
                      onChange={(_e, value) =>
                        setFieldValue("rotationSchemeBranches", value)
                      }
                      selectedValues={values.rotationSchemeBranches}
                      mapper={(data) => data}
                      disabled={!restrictBranches}
                      filter={{
                        pageSize: 25,
                        pageIndex: 1,
                        filterBy: null,
                        orderBy: null,
                      }}
                      multiple
                    />
                  </Box>
                </Box>
                <Box
                  display="flex"
                  alignItems={"center"}
                  className={styles.fieldWrapper}
                >
                  <span
                    className={clsx(
                      styles.radioGroupLabel,
                      styles.alignSelfStart
                    )}
                  >
                    {translate("restrict-to-teams")}
                  </span>
                  <Box className={styles.fieldContainer}>
                    <CustomSwitch
                      checked={!restrictBranches}
                      onChange={() => {
                        setFieldValue("rotationSchemeTeams", []);
                        setFieldValue("rotationSchemeCourses", []);
                        setFieldValue("rotationSchemeBranches", []);
                        setRestrictBranches(!restrictBranches);
                      }}
                    />
                    <CustomAsyncAutocomplete
                      rootCustomClasses={styles.inputwrapper}
                      placeholder={translate("teams-input-placeholder")}
                      autocompleteClasses={clsx({
                        [styles.disabled]: restrictBranches,
                      })}
                      disabled={restrictBranches}
                      onFetchData={(filter) =>
                        GetTeam(...Object.values(filter))
                      }
                      chipsLabel={(option) =>
                        option.teamsName || option.teamName || ""
                      }
                      onChange={(_e, value) =>
                        setFieldValue("rotationSchemeTeams", value)
                      }
                      selectedValues={values.rotationSchemeTeams}
                      displayLabel={(option) =>
                        option.teamsName || option.teamName || ""
                      }
                      getOptionSelected={(option, value) =>
                        value.teamsId === option.teamsId ||
                        value.teamId === option.teamsId
                      }
                      mapper={(data) => data}
                      filter={{
                        pageIndex: 1,
                        pageSize: 25,
                        search: "",
                      }}
                      multiple
                    />
                  </Box>
                </Box>
                <Box
                  display="flex"
                  alignItems={"center"}
                  className={styles.fieldWrapper}
                >
                  <span
                    className={clsx(
                      styles.radioGroupLabel,
                      styles.alignSelfStart
                    )}
                  >
                    {translate("agent-response-time")}
                  </span>
                  <Box className={styles.fieldContainer}>
                    <ReskinningInputs
                      fieldClasses={styles.inputwrapper}
                      value={values.sla}
                      type="number"
                      onInputChanged={handleChange}
                    />
                  </Box>
                </Box>
                <Box
                  display="flex"
                  alignItems={"center"}
                  className={styles.fieldWrapper}
                >
                  <span
                    className={clsx(
                      styles.radioGroupLabel,
                      styles.alignSelfStart
                    )}
                  >
                    {translate("courses-label")}
                  </span>
                  <Box className={styles.fieldContainer}>
                    <CustomAsyncAutocomplete
                      rootCustomClasses={styles.inputwrapper}
                      placeholder={"Select Multiple Courses"}
                      autocompleteClasses={clsx({
                        [styles.disabled]: restrictBranches,
                      })}
                      disabled={restrictBranches}
                      onFetchData={GetAllLMSCourses}
                      onChange={(_e, value) =>
                        setFieldValue("rotationSchemeCourses", value)
                      }
                      selectedValues={values.rotationSchemeCourses}
                      filter={{
                        pageIndex: 1,
                        pageSize: 100,
                        search: "",
                      }}
                      multiple
                      chipsLabel={(option) => option?.lmsCourseName}
                      displayLabel={(option) => option?.lmsCourseName}
                      mapper={(data) => data}
                    />
                  </Box>
                </Box>
                {values.rotationSchemeCourses.map((course, index) => (
                  <Box
                    display="flex"
                    marginTop={"24px"}
                    className={styles.scoreWrapper}
                    key={course.lmsCourseId}
                  >
                    <span
                      className={clsx(
                        styles.radioGroupLabel,
                        styles.alignSelfStart
                      )}
                    >
                      {course.lmsCourseName}
                    </span>
                    <Box display={"flex"} className={styles.scoreContainer}>
                      <label className={styles.scoreLabel}>
                        {translate("score-min")}
                      </label>
                      <ReskinningInputs
                        type="number"
                        onInputChanged={(e) => {
                          const courses = [...values.rotationSchemeCourses];
                          courses[index] = {
                            ...courses[index],
                            scoreFrom: e.target.value,
                          };

                          setFieldValue("rotationSchemeCourses", courses);
                        }}
                        fieldClasses={styles.inputwrapper}
                        min={0}
                        defaultValue={0}
                      />
                      <label className={styles.scoreLabel}>
                        {translate("score-max")}
                      </label>
                      <ReskinningInputs
                        onInputChanged={(e) => {
                          const courses = [...values.rotationSchemeCourses];
                          courses[index] = {
                            ...courses[index],
                            scoreTo: e.target.value,
                          };
                          setFieldValue("rotationSchemeCourses", courses);
                        }}
                        fieldClasses={styles.inputwrapper}
                        max={100}
                        type="number"
                        defaultValue={100}
                      />
                    </Box>
                  </Box>
                ))}
              </CustomCard>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
}

export default SchemaSettingsForm;
